import { Component, computed, effect, ErrorHandler, inject, input, signal } from '@angular/core';
import type { MermaidConfig } from 'mermaid';
import { MarkdownComponent } from 'ngx-markdown';
import { LoaderComponent } from 'src/app/modules/shared/components/loader.component';

type Mermaid = typeof import('mermaid').default;

const MERMAID_BLOCK_REGEX = /```[ \t]*mermaid[^\r\n]*\r?\n[\s\S]*?```/gi;
const XY_CHART_X_AXIS_REGEX = /^\s*x-axis\b[^\[\r\n]*(\[[^\]\r\n]*\])/gim;
const MERMAID_LOAD_ERROR_MESSAGE =
  '<p class="mermaid-load-error">We’re having trouble displaying the graph. Please refresh the page and try again.</p>';
const XY_CHART_WIDTH = 470;
const XY_CHART_PLOT_WIDTH_RATIO = 0.8;
const XY_CHART_X_AXIS_LABEL_FONT_SIZE = 13;
const XY_CHART_X_AXIS_LABEL_GAP = 4;
const XY_CHART_X_AXIS_CROWDED_ROTATION = -45;

export const MERMAID_CONFIG = {
  securityLevel: 'strict',
  startOnLoad: false,
  fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
  themeVariables: {
    pieTitleTextSize: '26px',
    pieSectionTextSize: '20px',
    pieLegendTextSize: '20px',
    pie1: '#4E79A7',
    pie2: '#F28E2B',
    pie3: '#E15759',
    pie4: '#76B7B2',
    pie5: '#59A14F',
    pie6: '#EDC948',
    pie7: '#B07AA1',
    pie8: '#FF9DA7',
    pie9: '#9C755F',
    pie10: '#BAB0AC',
    pie11: '#2F4B7C',
    pie12: '#A05195',
    xyChart: {
      plotColorPalette: '#6C5CE7, #0077B6, #D1495B, #2A9D8F, #B5651D, #7B2CBF',
    },
  },
  pie: {
    textPosition: 0.75,
    legendPosition: 'bottom',
  },
  xyChart: {
    width: XY_CHART_WIDTH,
    height: 300,
    titleFontSize: 18,
    xAxis: {
      labelFontSize: XY_CHART_X_AXIS_LABEL_FONT_SIZE,
      titleFontSize: 14,
    },
    yAxis: {
      labelFontSize: 13,
      titleFontSize: 14,
    },
  },
} satisfies MermaidConfig;

@Component({
  selector: 'app-md-viewer',
  standalone: true,
  imports: [MarkdownComponent, LoaderComponent],
  templateUrl: './md-viewer.component.html',
})
export class MdViewerComponent {
  private errorHandler = inject(ErrorHandler);
  private isMermaidLoading = false;

  content = input.required<string>();
  openLinksInNewTab = input(false);
  hasMermaid = computed(() => /```[ \t]*mermaid(?:[ \t]*\r?\n|[ \t]*$)/im.test(this.content()));
  mermaidFallbackContent = computed(() =>
    this.content().replace(MERMAID_BLOCK_REGEX, MERMAID_LOAD_ERROR_MESSAGE),
  );
  mermaidOptions = computed<MermaidConfig>(() => ({
    ...MERMAID_CONFIG,
    xyChart: {
      ...MERMAID_CONFIG.xyChart,
      xAxis: {
        ...MERMAID_CONFIG.xyChart.xAxis,
        labelRotation: hasCrowdedXAxis(this.content()) ? XY_CHART_X_AXIS_CROWDED_ROTATION : 0,
      },
    },
  }));
  isMermaidReady = signal(false);
  isMermaidLoadFailed = signal(false);

  constructor() {
    effect(() => {
      if (this.hasMermaid()) {
        void this.loadMermaid();
      }
    });
  }

  onClick(event: MouseEvent): void {
    if (!this.openLinksInNewTab()) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      event.preventDefault();
      window.open((target as HTMLAnchorElement).href, '_blank', 'noopener,noreferrer');
    }
  }

  private async loadMermaid(): Promise<void> {
    if (this.isMermaidReady() || this.isMermaidLoading) {
      return;
    }

    this.isMermaidLoading = true;
    this.isMermaidLoadFailed.set(false);

    try {
      const { default: mermaid } = await import('mermaid');
      (globalThis as typeof globalThis & { mermaid: Mermaid }).mermaid = mermaid;
      this.isMermaidReady.set(true);
    } catch (error) {
      this.isMermaidLoadFailed.set(true);
      this.errorHandler.handleError(error);
    } finally {
      this.isMermaidLoading = false;
    }
  }
}

function hasCrowdedXAxis(markdown: string): boolean {
  for (const match of markdown.matchAll(XY_CHART_X_AXIS_REGEX)) {
    const labels = parseXAxisLabels(match[1]);
    if (labels.length < 2) {
      continue;
    }

    const labelSpacing = (XY_CHART_WIDTH * XY_CHART_PLOT_WIDTH_RATIO) / (labels.length - 1);
    const labelWidths = labels.map(estimateLabelWidth);
    const hasOverlappingLabels = labelWidths.slice(1).some((labelWidth, index) => {
      const precedingLabelWidth = labelWidths[index];
      return (precedingLabelWidth + labelWidth) / 2 + XY_CHART_X_AXIS_LABEL_GAP > labelSpacing;
    });

    if (hasOverlappingLabels) {
      return true;
    }
  }

  return false;
}

function parseXAxisLabels(serializedLabels: string): string[] {
  try {
    const labels: unknown = JSON.parse(serializedLabels);
    return Array.isArray(labels) && labels.every((label) => typeof label === 'string') ? labels : [];
  } catch {
    return [];
  }
}

function estimateLabelWidth(label: string): number {
  return label.length * XY_CHART_X_AXIS_LABEL_FONT_SIZE * 0.6;
}
